import { ReportingState, PlayerEvent } from '../reporter/constants';
import { ReporterState } from '../state/LocalState';
import { PLAYER_EVENTS_BY_CODE } from '../common/Reporter/constants';

const {
  CLEANED,
  ENDED,
  PAUSED,
  PAUSING,
  PLAY,
  PLAYING,
  RESTART,
  WAITING
} = ReportingState;

export const isReporting = (state: ReporterState): boolean => {
  const states = [PAUSING, PLAY, PLAYING, RESTART];
  return compareStates(states, true, state.reportingState);
};
export const isStartable = (state: ReporterState): boolean => {
  const states = [PAUSED, PAUSING, PLAY];
  return compareStates(states, true, state.reportingState);
};
export const couldReport = (state: ReporterState): boolean => {
  const states = [ENDED, PAUSED];
  return (
    compareStates(states, false, state.reportingState) &&
    !state.reportSetTimeoutId
  );
};

export const isStartableFromDataRecord = (state: ReporterState): boolean => {
  const states = [PLAY];
  const reportingState = state.reportingState;
  const intervalId = state.reportIntervalId;
  return compareStates(states, true, reportingState) && !intervalId;
};
export const compareStates = (
  states: ReportingState[],
  noStateTrue: boolean,
  state?: ReportingState
): boolean => {
  return typeof state != 'undefined'
    ? states.indexOf(state) != -1
    : noStateTrue;
};
export const sendImmediateReport = (
  state: ReportingState,
  desired: ReportingState
): boolean => {
  // Should we send an immediate report when transitioning from state to
  // desired state?
  const sendImmediate: Record<ReportingState, ReportingState[]> = {
    [CLEANED]: [],
    [ENDED]: [],
    [PAUSED]: [],
    [PAUSING]: [PAUSED, ENDED, CLEANED],
    [PLAY]: [PLAYING, ENDED, CLEANED],
    [PLAYING]: [ENDED, CLEANED],
    [RESTART]: [ENDED, CLEANED],
    [WAITING]: []
  };
  return compareStates(sendImmediate[state], false, desired);
};

export const canTransition = (
  reporterState: ReporterState,
  desired: ReportingState,
  transitionIfAble?: boolean,
  dontAllowTransitionToSame?: boolean
): boolean => {
  const state = reporterState.reportingState;
  if (!dontAllowTransitionToSame && state === desired) {
    return true;
  }
  // NOTE: transition to state CLEANED does not use this function.
  const allowedTransitions: Record<ReportingState, ReportingState[]> = {
    [CLEANED]: [],
    [ENDED]: [RESTART, CLEANED],
    [PAUSED]: [PLAY, PLAYING, ENDED, CLEANED],
    [PAUSING]: [PAUSED, PLAY, PLAYING, ENDED, CLEANED],
    [PLAY]: [PLAYING, PAUSING, ENDED, CLEANED],
    [PLAYING]: [PAUSING, ENDED, CLEANED],
    [RESTART]: [PLAY, PLAYING, ENDED, CLEANED],
    [WAITING]: [ENDED, CLEANED] // changed in Reporter when received a play event
    // Only want to change the reporting state from WAITING in that one place.
  };
  const can = compareStates(allowedTransitions[state], false, desired);
  if (can && transitionIfAble) {
    reporterState.reportingState = desired;
  }
  return can;
};

interface TransitionState {
  previous?: unknown;
  current?: unknown;
}
export const transitionAfterSendReport = (
  state: ReporterState
): TransitionState => {
  const previous = ReportingState[state.reportingState];
  switch (state.reportingState) {
    case ReportingState.RESTART:
    case ReportingState.PLAY:
      canTransition(state, ReportingState.PLAYING, true);
      break;
    case ReportingState.PAUSING:
      state.reportingState = ReportingState.PAUSED;
      break;
  }
  return { previous, current: ReportingState[state.reportingState] };
};

export const shouldReportNow = (
  evt: PlayerEvent,
  state: ReporterState
): boolean => {
  // There is the event, what state we are in
  const event = PLAYER_EVENTS_BY_CODE[evt.e];
  switch (event.name) {
    case 'play':
    case 'playing':
      return state.reportingState === PLAYING;
    case 'beforeunload':
    case 'pagehide':
    case 'playerdispose':
      return state.reportingState === ENDED;
    case 'pause':
      return state.reportingState === PAUSING;
  }
  return false;
};
